import * as React from "react";
import { IZone } from "@nju/data-access-portal-dashboard";
import {
  IMobileOrchestrator,
  IMultiOrderOrchestrator,
} from "@nju/data-access-portal-orchestrator";
import { IDataPackage, IVoicePackage } from "@nju/data-access-portal-product";
import { pushSimpleEvent } from "@nju/data-layer";
import { Icon } from "@nju/icons";
import { portalRoutes } from "@nju/routes";
import { Panel, Separator, Text, Tooltip } from "@nju/ui";
import {
  formatDate,
  parseMegabytesToGigabytes,
  parseMinutesToSeconds,
  parseSecondsToMinutes,
} from "@nju/utils";
import { Button } from "./partials";

interface IRoamingPackagesProps {
  orchestrator: IMobileOrchestrator | IMultiOrderOrchestrator | undefined;
  dataPackage: IDataPackage | undefined;
  zones: IZone[] | undefined;
  voicePackage: IVoicePackage | undefined;
}

export function RoamingPackages({
  dataPackage,
  voicePackage,
  orchestrator,
  zones,
}: IRoamingPackagesProps) {
  const { currentRoamingGigabytes, startingRoamingGigabytes } =
    React.useMemo(() => {
      if (!dataPackage?.roaming) {
        return {
          currentRoamingGigabytes: undefined,
          startingRoamingGigabytes: undefined,
        };
      }

      const currentRoamingGigabytes = parseMegabytesToGigabytes(
        dataPackage.roaming.currentValue,
      );
      const startingRoamingGigabytes = parseMegabytesToGigabytes(
        dataPackage.roaming.startingValue,
      );

      return {
        currentRoamingGigabytes,
        startingRoamingGigabytes,
      };
    }, [dataPackage]);

  const { currentSTDRoamingGigabytes, startingSTDRoamingGigabytes } =
    React.useMemo(() => {
      if (!dataPackage?.roamingSTDPackage) {
        return {
          currentSTDRoamingGigabytes: undefined,
          startingSTDRoamingGigabytes: undefined,
        };
      }

      const currentSTDRoamingGigabytes = parseMegabytesToGigabytes(
        dataPackage.roamingSTDPackage.balances[0].currentValue,
      );
      const startingSTDRoamingGigabytes = parseMegabytesToGigabytes(
        dataPackage.roamingSTDPackage.balances[0].startingValue,
      );

      return {
        currentSTDRoamingGigabytes,
        startingSTDRoamingGigabytes,
      };
    }, [dataPackage]);

  const { currentEXORoamingGigabytes, startingEXORoamingGigabytes } =
    React.useMemo(() => {
      if (!dataPackage?.roamingEXOPackage) {
        return {
          currentEXORoamingGigabytes: undefined,
          startingEXORoamingGigabytes: undefined,
        };
      }

      const currentEXORoamingGigabytes = parseMegabytesToGigabytes(
        dataPackage.roamingEXOPackage.balances[0].currentValue,
      );
      const startingEXORoamingGigabytes = parseMegabytesToGigabytes(
        dataPackage.roamingEXOPackage.balances[0].startingValue,
      );

      return {
        currentEXORoamingGigabytes,
        startingEXORoamingGigabytes,
      };
    }, [dataPackage]);

  const { currentBREXITRoamingGigabytes, startingBREXITRoamingGigabytes } =
    React.useMemo(() => {
      if (!dataPackage?.roamingBREXITPackage) {
        return {
          currentBREXITRoamingGigabytes: undefined,
          startingBREXITRoamingGigabytes: undefined,
        };
      }

      const currentBREXITRoamingGigabytes = parseMegabytesToGigabytes(
        dataPackage.roamingBREXITPackage.balances[0].currentValue,
      );
      const startingBREXITRoamingGigabytes = parseMegabytesToGigabytes(
        dataPackage.roamingBREXITPackage.balances[0].startingValue,
      );

      return {
        currentBREXITRoamingGigabytes,
        startingBREXITRoamingGigabytes,
      };
    }, [dataPackage]);

  const { currentHTRoamingGigabytes, startingHTRoamingGigabytes } =
    React.useMemo(() => {
      if (!dataPackage?.roamingHTPackage) {
        return {
          currentHTRoamingGigabytes: undefined,
          startingHTRoamingGigabytes: undefined,
        };
      }

      const currentHTRoamingGigabytes = parseMegabytesToGigabytes(
        dataPackage.roamingHTPackage.balances[0].currentValue,
      );
      const startingHTRoamingGigabytes = parseMegabytesToGigabytes(
        dataPackage.roamingHTPackage.balances[0].startingValue,
      );

      return {
        currentHTRoamingGigabytes,
        startingHTRoamingGigabytes,
      };
    }, [dataPackage]);

  const roamingSTDVoice = voicePackage?.roamingCalls?.find((intPackage) => {
    return intPackage.zone === "STD";
  });

  const roamingEXOVoice = voicePackage?.roamingCalls?.find((intPackage) => {
    return intPackage.zone === "EXO";
  });

  const roamingBREXITVoice = voicePackage?.roamingCalls?.find((intPackage) => {
    return intPackage.zone === "BREXIT";
  });

  const euRoamingZoneName = zones?.find((zone) => {
    return zone.category === "ROAMING" && zone.name === "EU";
  })?.displayName.pl;
  const stdRoamingZoneName = zones?.find((zone) => {
    return zone.category === "ROAMING" && zone.name === "STD";
  })?.displayName.pl;
  const exoRoamingZoneName = zones?.find((zone) => {
    return zone.category === "ROAMING" && zone.name === "EXO";
  })?.displayName.pl;
  const brexitRoamingZoneName = zones?.find((zone) => {
    return zone.category === "ROAMING" && zone.name === "BREXIT";
  })?.displayName.pl;
  const htRoamingZoneName = zones?.find((zone) => {
    return zone.category === "ROAMING" && zone.name === "HT";
  })?.displayName.pl;

  const handlePushDataLayer = React.useCallback((eventName: string) => {
    pushSimpleEvent({ event: "activity_service", eventName });
  }, []);

  return (
    <Panel
      iconName="earth-europe"
      isFoldable={true}
      testId="roaming-tab"
      title="roaming"
    >
      {({ isExpanded }) => {
        return (
          <>
            <Text
              size={{
                "@onlyMobile": "m",
                "@fromTablet": "l",
              }}
            >
              Twój limit GB w UE i informacje o aktywnych pakietach do
              wykorzystania, gdy jesteś za granicą
            </Text>
            {isExpanded && (
              <>
                <Separator size="s" />
                <Text
                  size={{
                    "@onlyMobile": "m",
                    "@fromTablet": "l",
                  }}
                  weight="bold"
                >
                  {euRoamingZoneName} - w ramach Twojej usługi
                </Text>
                <Separator size="xxs" tone="invisible" />
                {startingRoamingGigabytes && currentRoamingGigabytes && (
                  <>
                    <Text className="mb-1" display="block" size="s">
                      pozostało
                    </Text>
                    <Separator size="none" tone="invisible" />
                    <Text
                      size={{
                        "@onlyMobile": "s",
                        "@fromTablet": "l",
                      }}
                      testId="ue-roaming-tariff-gb"
                    >
                      <Text
                        size={{
                          "@onlyMobile": "m",
                          "@fromTablet": "keyM",
                        }}
                        weight="bold"
                      >
                        {currentRoamingGigabytes} GB{" "}
                      </Text>
                      z {startingRoamingGigabytes} GB
                    </Text>
                  </>
                )}
                {dataPackage?.anniversaryDate && (
                  <Text className="mt-1" display="block" size="s">
                    ważny do końca okresu rozliczeniowego{" "}
                    {formatDate(dataPackage.anniversaryDate, true, ".", ", ")},
                    odnawia się co miesiąc
                  </Text>
                )}
                {(roamingSTDVoice ||
                  (currentSTDRoamingGigabytes &&
                    startingSTDRoamingGigabytes)) && (
                  <>
                    <Separator size="s" />
                    <Text
                      size={{
                        "@onlyMobile": "m",
                        "@fromTablet": "l",
                      }}
                      weight="bold"
                    >
                      {stdRoamingZoneName} - dokupiony pakiet
                    </Text>
                    <Separator size="xxs" tone="invisible" />
                    <Text className="mb-1" display="block" size="s">
                      pozostało
                    </Text>
                    {currentSTDRoamingGigabytes && startingSTDRoamingGigabytes && (
                      <>
                        <Separator size="none" tone="invisible" />
                        <Text
                          size={{
                            "@onlyMobile": "s",
                            "@fromTablet": "l",
                          }}
                          testId="standard-roaming-tariff-gb"
                        >
                          <Text
                            size={{
                              "@onlyMobile": "m",
                              "@fromTablet": "keyM",
                            }}
                            weight="bold"
                          >
                            {currentSTDRoamingGigabytes} GB{" "}
                          </Text>
                          z {startingSTDRoamingGigabytes} GB
                        </Text>
                      </>
                    )}
                    {dataPackage?.roamingSTDPackage?.expiryDate && (
                      <Text className="mt-1" display="block" size="s">
                        pakiet będzie ważny do{" "}
                        {formatDate(
                          dataPackage.roamingSTDPackage.expiryDate,
                          true,
                          ".",
                          ", ",
                        )}
                      </Text>
                    )}
                    {roamingSTDVoice?.balances && (
                      <>
                        {currentSTDRoamingGigabytes &&
                          startingSTDRoamingGigabytes && (
                            <Separator size="s" tone="invisible" />
                          )}
                        <Text
                          size={{
                            "@onlyMobile": "s",
                            "@fromTablet": "l",
                          }}
                          testId="standard-roaming-tariff-minutes"
                        >
                          <Text
                            size={{
                              "@onlyMobile": "m",
                              "@fromTablet": "keyM",
                            }}
                            weight="bold"
                          >
                            {parseMinutesToSeconds(
                              roamingSTDVoice.balances[0].currentValue,
                            )}{" "}
                          </Text>
                          z{" "}
                          {parseSecondsToMinutes(
                            roamingSTDVoice.balances[0].startingValue,
                          )}{" "}
                          min./SMS/MMS
                        </Text>
                        <div className="inline ml-2">
                          <Tooltip content="1 minuta połączenia wychodzącego to równowartość 1 SMS-a lub 1 MMS-a. SMS-y i MMS-y, które odbierasz w roamingu nie pomniejszają wykupionego pakietu. Połączenia rozliczane są sekundowo.">
                            <Icon
                              height={17}
                              name="info-circle"
                              tone="accent"
                              width={17}
                            />
                          </Tooltip>
                        </div>
                      </>
                    )}
                    {roamingSTDVoice?.expiryDate && (
                      <Text className="mt-1" display="block" size="s">
                        pakiet będzie ważny do{" "}
                        {formatDate(
                          roamingSTDVoice.expiryDate,
                          true,
                          ".",
                          ", ",
                        )}
                      </Text>
                    )}
                  </>
                )}
                {(roamingEXOVoice ||
                  (currentEXORoamingGigabytes &&
                    startingEXORoamingGigabytes)) && (
                  <>
                    <Separator size="s" />
                    <Text
                      size={{
                        "@onlyMobile": "m",
                        "@fromTablet": "l",
                      }}
                      weight="bold"
                    >
                      {exoRoamingZoneName} - dokupiony pakiet
                    </Text>
                    <Separator size="xxs" tone="invisible" />
                    <Text className="mb-1" display="block" size="s">
                      pozostało
                    </Text>
                    {startingEXORoamingGigabytes && currentEXORoamingGigabytes && (
                      <>
                        <Separator size="none" tone="invisible" />
                        <Text
                          size={{
                            "@onlyMobile": "s",
                            "@fromTablet": "l",
                          }}
                          testId="exotic-roaming-tariff-gb"
                        >
                          <Text
                            size={{
                              "@onlyMobile": "m",
                              "@fromTablet": "keyM",
                            }}
                            weight="bold"
                          >
                            {currentEXORoamingGigabytes} GB{" "}
                          </Text>
                          z {startingEXORoamingGigabytes} GB
                        </Text>
                      </>
                    )}
                    {dataPackage?.roamingEXOPackage?.expiryDate && (
                      <Text className="mt-1" display="block" size="s">
                        pakiet będzie ważny do{" "}
                        {formatDate(
                          dataPackage.roamingEXOPackage.expiryDate,
                          true,
                          ".",
                          ", ",
                        )}
                      </Text>
                    )}
                    {roamingEXOVoice?.balances && (
                      <>
                        {currentEXORoamingGigabytes &&
                          startingEXORoamingGigabytes && (
                            <Separator size="s" tone="invisible" />
                          )}
                        <Text
                          size={{
                            "@onlyMobile": "s",
                            "@fromTablet": "l",
                          }}
                          testId="exotic-roaming-tariff-minutes"
                        >
                          <Text
                            size={{
                              "@onlyMobile": "m",
                              "@fromTablet": "keyM",
                            }}
                            weight="bold"
                          >
                            {parseMinutesToSeconds(
                              roamingEXOVoice.balances[0].currentValue,
                            )}{" "}
                          </Text>
                          z{" "}
                          {parseSecondsToMinutes(
                            roamingEXOVoice.balances[0].startingValue,
                          )}{" "}
                          min./SMS/MMS
                        </Text>
                        <div className="inline ml-2">
                          <Tooltip content="1 minuta połączenia wychodzącego to równowartość 1 SMS-a lub 1 MMS-a. SMS-y i MMS-y, które odbierasz w roamingu nie pomniejszają wykupionego pakietu. Połączenia rozliczane są sekundowo.">
                            <Icon
                              height={17}
                              name="info-circle"
                              tone="accent"
                              width={17}
                            />
                          </Tooltip>
                        </div>
                      </>
                    )}
                    {roamingEXOVoice?.expiryDate && (
                      <Text className="mt-1" display="block" size="s">
                        pakiet będzie ważny do{" "}
                        {formatDate(
                          roamingEXOVoice.expiryDate,
                          true,
                          ".",
                          ", ",
                        )}
                      </Text>
                    )}
                  </>
                )}
                {(roamingBREXITVoice ||
                  (currentBREXITRoamingGigabytes &&
                    startingBREXITRoamingGigabytes)) && (
                  <>
                    <Separator size="s" />
                    <Text
                      size={{
                        "@onlyMobile": "m",
                        "@fromTablet": "l",
                      }}
                      weight="bold"
                    >
                      {brexitRoamingZoneName} - dokupiony pakiet
                    </Text>
                    <Separator size="xxs" tone="invisible" />
                    {currentBREXITRoamingGigabytes &&
                      startingBREXITRoamingGigabytes && (
                        <>
                          <Text className="mb-1" display="block" size="s">
                            pozostało
                          </Text>
                          <Separator size="none" tone="invisible" />
                          <Text
                            size={{
                              "@onlyMobile": "s",
                              "@fromTablet": "l",
                            }}
                            testId="great-britain-roaming-tariff-gb"
                          >
                            <Text
                              size={{
                                "@onlyMobile": "m",
                                "@fromTablet": "keyM",
                              }}
                              weight="bold"
                            >
                              {currentBREXITRoamingGigabytes} GB{" "}
                            </Text>
                            z {startingBREXITRoamingGigabytes} GB
                          </Text>
                        </>
                      )}
                    {dataPackage?.roamingBREXITPackage?.expiryDate && (
                      <Text className="mt-1" display="block" size="s">
                        pakiet będzie ważny do{" "}
                        {formatDate(
                          dataPackage.roamingBREXITPackage.expiryDate,
                          true,
                          ".",
                          ", ",
                        )}
                      </Text>
                    )}
                    <Separator size="s" tone="invisible" />
                    <Text
                      size={{
                        "@onlyMobile": "m",
                        "@fromTablet": "l",
                      }}
                      testId="great-britain-roaming-tariff-minutes"
                      weight="bold"
                    >
                      minuty / SMS / MMS bez limitu
                    </Text>
                    {roamingBREXITVoice?.expiryDate && (
                      <Text className="mt-1" display="block" size="s">
                        pakiet będzie ważny do{" "}
                        {formatDate(
                          roamingBREXITVoice.expiryDate,
                          true,
                          ".",
                          ", ",
                        )}
                      </Text>
                    )}
                  </>
                )}
                {dataPackage?.roamingHTPackage && (
                  <>
                    <Separator size="s" />
                    <Text
                      size={{
                        "@onlyMobile": "m",
                        "@fromTablet": "l",
                      }}
                      weight="bold"
                    >
                      {htRoamingZoneName} - dokupiony pakiet
                    </Text>
                    <Separator size="xxs" tone="invisible" />
                    {currentHTRoamingGigabytes && startingHTRoamingGigabytes && (
                      <>
                        <Text className="mb-1" display="block" size="s">
                          pozostało
                        </Text>
                        <Separator size="none" tone="invisible" />
                        <Text
                          size={{
                            "@onlyMobile": "s",
                            "@fromTablet": "l",
                          }}
                          testId="promo-roaming-tariff-gb"
                        >
                          <Text
                            size={{
                              "@onlyMobile": "m",
                              "@fromTablet": "keyM",
                            }}
                            weight="bold"
                          >
                            {currentHTRoamingGigabytes} GB{" "}
                          </Text>
                          z {startingHTRoamingGigabytes} GB
                        </Text>
                      </>
                    )}
                    {dataPackage?.roamingHTPackage?.expiryDate && (
                      <Text className="mt-1" display="block" size="s">
                        pakiet będzie ważny do{" "}
                        {formatDate(
                          dataPackage.roamingHTPackage.expiryDate,
                          true,
                          ".",
                          ", ",
                        )}
                      </Text>
                    )}
                  </>
                )}
                <div className="mt-5 flex justify-start">
                  <Button
                    iconName="cart-plus"
                    onClick={() => {
                      handlePushDataLayer("additional_packs");
                    }}
                    path={portalRoutes.mojeKonto.uslugiDodatkowe.roaming.index.get(
                      {
                        ...(orchestrator?.userId && {
                          userId: orchestrator.userId,
                        }),
                      },
                    )}
                    title="dodatkowe pakiety"
                  />
                </div>
              </>
            )}
          </>
        );
      }}
    </Panel>
  );
}
